import {
  type Dispatch,
  type MouseEventHandler,
  type SetStateAction,
  useCallback,
} from 'react'
import { useTransitionState } from 'react-transition-state'
import { css } from '@emotion/react'
import { TransitionDurationToken } from '@livechat/design-system-react-components'

import type { IAgentItem } from '@/store/agents/interfaces'
import { PAYMENT } from '@/constants'

import AgentList from './AgentList'
import ExtendListButton from './ExtendListButton'

const stackedAgentsCss = css`
  overflow: auto;
`

const extendedAgentsCss = (agentCount: number) => css`
  .extended-agents {
    transition: all var(${TransitionDurationToken.Fast2}) ease-in-out;
    opacity: 1;
    height: calc(${agentCount * 51}px - 10px);

    &.preEnter,
    &.exiting {
      height: 0;
      opacity: 0;
    }

    &.exited {
      display: none;
    }
  }
`

interface Props {
  recipients: string[]
  setRecipients: Dispatch<SetStateAction<string[]>>
  agents: IAgentItem[]
  className?: string
}

export const StackedAgentList = ({
  recipients,
  setRecipients,
  agents,
  className,
}: Props) => {
  const [{ status, isEnter }, toggle] = useTransitionState({
    timeout: 200, // TransitionDurationToken.Fast2 - 200ms
    preEnter: true,
  })

  const updateRecipients = useCallback(
    (recipient: string) => {
      const recipientIndex = recipients.indexOf(recipient)
      const exists = recipientIndex > -1
      const newRecipients = [...recipients]

      if (!exists) {
        newRecipients.push(recipient)
      } else {
        newRecipients.splice(recipientIndex, 1)
      }

      setRecipients(newRecipients)
    },
    [recipients, setRecipients]
  )

  const topAgents = agents.slice(0, PAYMENT.NO_ACCESS_MAX_AGENTS)
  const extendedAgents =
    agents.length > PAYMENT.NO_ACCESS_MAX_AGENTS
      ? agents.slice(PAYMENT.NO_ACCESS_MAX_AGENTS, agents.length)
      : []

  const handleExtendList: MouseEventHandler = (e) => {
    e.preventDefault()
    toggle()
  }

  return (
    <div
      css={stackedAgentsCss}
      className={className}
      data-test="stacked-agent-list"
    >
      {!!agents?.length && (
        <AgentList
          agents={topAgents}
          recipients={recipients}
          updateRecipients={updateRecipients}
        />
      )}

      {agents.length > PAYMENT.NO_ACCESS_MAX_AGENTS && (
        <div css={extendedAgentsCss(extendedAgents.length)}>
          <ExtendListButton
            isExtended={isEnter}
            handleExtend={handleExtendList}
            agentCount={extendedAgents.length}
          />

          <div className={`extended-agents ${status}`}>
            <AgentList
              agents={extendedAgents}
              recipients={recipients}
              updateRecipients={updateRecipients}
            />
          </div>
        </div>
      )}
    </div>
  )
}
